//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { CAPTION_LENGTH_REMAINING_WARNING_THRESHOLD } from '@tiktok/components/schedule/post-details/tiktokPostDetailsConstants';
import {
  CAPTION_LENGTH_LIMIT,
  TIKTOK_MAX_VIDEO_DURATION_MILLIS,
  TIKTOK_MIN_VIDEO_DURATION_MILLIS,
  SECONDS_TO_MILLISECONDS_CONVERSION_FACTOR,
} from '@tiktok/redux/schedule/post-details/video-automatic/tiktokVideoAutomaticPostDetailsModel';

/**
 * We create a default caption from the original caption with a
 * username attribution to help clients easily repost UGC.
 */
export const createDefaultCaption = (mediaItem?: IMlsMediaItem) => {
  const caption = mediaItem?.networkItem?.caption;
  const postingUsername = mediaItem?.networkUser?.username;

  if (caption) {
    return postingUsername ? `${caption} (via @${postingUsername})` : caption;
  }
};

export const getCaptionCharacterCountStatus = (caption: string) => {
  const count = caption.length;

  const remainingCharacterCount = CAPTION_LENGTH_LIMIT - count;
  const isAboveLimit = remainingCharacterCount < 0;
  const countAboveLimit = isAboveLimit ? Math.abs(remainingCharacterCount) : 0;
  const isAboveWarningThreshold =
    remainingCharacterCount <= CAPTION_LENGTH_REMAINING_WARNING_THRESHOLD;

  return {
    count,
    countAboveLimit,
    remainingCharacterCount,
    isAboveLimit,
    isAboveWarningThreshold,
  };
};

export const getIsVideoDurationInvalid = (videoDurationMs: number = 0) => {
  return (
    videoDurationMs < TIKTOK_MIN_VIDEO_DURATION_MILLIS ||
    videoDurationMs > TIKTOK_MAX_VIDEO_DURATION_MILLIS
  );
};

export const getIsThumbnailOffsetInvalid = (
  videoDurationMs: number,
  offsetMs: number = 0,
) => {
  return offsetMs < 0 || offsetMs > videoDurationMs;
};

export const getThumbnailOffsetInSeconds = (offsetInMs: number = 0) =>
  offsetInMs / SECONDS_TO_MILLISECONDS_CONVERSION_FACTOR;

/**
 * If the input time has elapsed, return "Now + 1 minute"  instead.
 * This is used to handle scheduling edge cases.
 */
export const getFutureTimeToSetPost = (time: Date): Date => {
  if (time < new Date()) {
    return moment().add(1, 'minute').toDate();
  }

  return time;
};
